import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  useParams,
  Navigate,
} from "react-router-dom";
import { initGA, logAttributionOnce, logPageView } from "./utils/analytics";
import { getSeoForLocation, updateDocumentSeo } from "./utils/seo";
import "./index.css";
import Header from "./Header";
import FaqPage from "./pages/FaqPage";
import ContactPage from "./pages/ContactPage";
import TourDetails from "./TourDetails";
import Top from "./pages/Top";
import useScrollToTop from "./useScrollToTop";
import MiyakoGuideHome from "./MiyakoGuideHome/MiyakoGuideHome";
import GalleryPage from "./pages/GalleryPage";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import ToursPage from "./pages/TourPage";
import BlogPage from "./pages/BlogPage";
import BlogArchivePage from "./pages/BlogArchivePage";
import BlogArticle from "./pages/BlogArticle";
import ItineraryPlannerPage from "./pages/ItineraryPlannerPage";
import OceanGuidePage from "./pages/OceanGuidePage";
import DroneMapPage from "./pages/DroneMapPage";
import OceanGuideAdminPage from "./pages/OceanGuideAdminPage";
import OceanSearchLandingPage from "./pages/OceanSearchLandingPage";
import OceanPlacePage from "./pages/OceanPlacePage";
import HotelGuidePage from "./pages/HotelGuidePage";
import NotFoundPage from "./pages/NotFoundPage";
import WebProductionPage from "./pages/WebProductionPage";
import ShigiraConciergeDemoPage from "./pages/ShigiraConciergeDemoPage";
import MiyakoWorkDemoPage from "./pages/MiyakoWorkDemoPage";
import { registerServiceWorker } from "./serviceWorkerRegistration";

const PageMetaListener = () => {
  const location = useLocation();
  const { lang } = useLanguage();

  useEffect(() => {
    const seo = getSeoForLocation(location);
    updateDocumentSeo(seo);
  }, [location, lang]);

  useEffect(() => {
    const seo = getSeoForLocation(location);
    const pagePath = `${location.pathname}${location.search}`;

    logAttributionOnce();
    logPageView(pagePath, seo.title);
    // Only fire on real navigation (location), not on a UI language toggle —
    // otherwise GA4 sees a second page_view for the same URL and the
    // engagement/bounce accounting for that view gets reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  return null;
};

const LegacyTourRedirect = () => {
  const { id, locale } = useParams();
  const prefix = ["en", "zh", "ko"].includes(locale) ? `/${locale}` : "";

  return <Navigate to={`${prefix}/tours/${id}`} replace />;
};

const OceanPlaceLocaleRedirect = ({ collection }) => {
  const { slug } = useParams();
  return <Navigate to={`/ocean-guide/${collection}${slug ? `/${slug}` : ""}`} replace />;
};

const App = () => {
  useEffect(() => {
    initGA();
  }, []);
  useScrollToTop();

  return (
    <>
      <PageMetaListener />
      <Routes>
        <Route path="/" element={<Top />} />
        <Route path="/:locale" element={<Top />} />
        <Route
          path="/faq"
          element={
            <>
              <FaqPage />
            </>
          }
        />
        <Route
          path="/:locale/faq"
          element={
            <>
              <FaqPage />
            </>
          }
        />
        <Route
          path="/contact"
          element={
            <>
              <Header />
              <ContactPage />
            </>
          }
        />
        <Route
          path="/:locale/contact"
          element={
            <>
              <Header />
              <ContactPage />
            </>
          }
        />
        <Route
          path="/gallery"
          element={
            <>
              <GalleryPage />
            </>
          }
        />
        <Route
          path="/:locale/gallery"
          element={
            <>
              <GalleryPage />
            </>
          }
        />
        <Route
          path="/blog"
          element={
            <>
              <BlogPage />
            </>
          }
        />
        <Route
          path="/:locale/blog"
          element={
            <>
              <BlogPage />
            </>
          }
        />
        <Route
          path="/tours"
          element={
            <>
              <ToursPage />
            </>
          }
        />
        <Route
          path="/:locale/tours"
          element={
            <>
              <ToursPage />
            </>
          }
        />
        <Route path="/itinerary" element={<ItineraryPlannerPage />} />
        <Route path="/:locale/itinerary" element={<ItineraryPlannerPage />} />
        <Route path="/ocean-guide" element={<OceanGuidePage />} />
        <Route path="/ocean-guide/hotels" element={<HotelGuidePage />} />
        <Route path="/hotels" element={<Navigate to="/ocean-guide/hotels" replace />} />
        <Route path="/:locale/ocean-guide/hotels" element={<Navigate to="/ocean-guide/hotels" replace />} />
        <Route path="/:locale/ocean-guide" element={<OceanGuidePage />} />
        <Route path="/ocean-guide/spots" element={<OceanPlacePage collection="spots" />} />
        <Route path="/ocean-guide/spots/:slug" element={<OceanPlacePage collection="spots" />} />
        <Route path="/ocean-guide/restaurants" element={<OceanPlacePage collection="restaurants" />} />
        <Route path="/ocean-guide/restaurants/:slug" element={<OceanPlacePage collection="restaurants" />} />
        <Route path="/:locale/ocean-guide/spots" element={<OceanPlaceLocaleRedirect collection="spots" />} />
        <Route path="/:locale/ocean-guide/spots/:slug" element={<OceanPlaceLocaleRedirect collection="spots" />} />
        <Route path="/:locale/ocean-guide/restaurants" element={<OceanPlaceLocaleRedirect collection="restaurants" />} />
        <Route path="/:locale/ocean-guide/restaurants/:slug" element={<OceanPlaceLocaleRedirect collection="restaurants" />} />
        <Route path="/ocean-guide/snorkeling" element={<OceanSearchLandingPage pageKey="snorkeling" />} />
        <Route path="/ocean-guide/beach" element={<OceanSearchLandingPage pageKey="beach" />} />
        <Route path="/ocean-guide/stargazing" element={<OceanSearchLandingPage pageKey="stargazing" />} />
        <Route path="/ocean-guide/sunset" element={<OceanSearchLandingPage pageKey="sunset" />} />
        <Route path="/ocean-guide/snorkeling-today" element={<OceanSearchLandingPage pageKey="snorkeling-today" />} />
        <Route path="/ocean-guide/windy-day-beaches" element={<OceanSearchLandingPage pageKey="windy-day-beaches" />} />
        <Route path="/ocean-guide/rainy-day" element={<OceanSearchLandingPage pageKey="rainy-day" />} />
        <Route path="/ocean-guide/tomorrow-weather" element={<OceanSearchLandingPage pageKey="tomorrow-weather" />} />
        <Route path="/ocean-guide/sunset-today" element={<OceanSearchLandingPage pageKey="sunset-today" />} />
        <Route path="/ocean-guide/stargazing-tonight" element={<OceanSearchLandingPage pageKey="stargazing-tonight" />} />
        <Route path="/ocean-guide/after-typhoon-sea" element={<OceanSearchLandingPage pageKey="after-typhoon-sea" />} />
        <Route path="/admin" element={<OceanGuideAdminPage />} />
        <Route path="/web-production" element={<WebProductionPage />} />
        <Route path="/shigira-concierge-demo" element={<ShigiraConciergeDemoPage />} />
        <Route path="/miyako-work-demo" element={<MiyakoWorkDemoPage />} />
        <Route path="/drone-map" element={<DroneMapPage />} />
        <Route path="/:locale/drone-map" element={<DroneMapPage />} />
        <Route
          path="/blog/category/:categoryKey"
          element={<BlogArchivePage />}
        />
        <Route
          path="/:locale/blog/category/:categoryKey"
          element={<BlogArchivePage />}
        />
        <Route path="/blog/tag/:tagKey" element={<BlogArchivePage />} />
        <Route path="/:locale/blog/tag/:tagKey" element={<BlogArchivePage />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/:locale/blog/:slug" element={<BlogArticle />} />
        <Route
          path="/tours/:id"
          element={
            <>
              <Header />
              <TourDetails />
            </>
          }
        />
        <Route
          path="/:locale/tours/:id"
          element={
            <>
              <Header />
              <TourDetails />
            </>
          }
        />
        <Route path="/tour/:id" element={<LegacyTourRedirect />} />
        <Route path="/:locale/tour/:id" element={<LegacyTourRedirect />} />
        <Route
          path="/test"
          element={
            <>
              <MiyakoGuideHome />
            </>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <React.StrictMode>
    <LanguageProvider>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </LanguageProvider>
  </React.StrictMode>,
);

registerServiceWorker();
